'use client';

import {
  Wallet, Clock, TrendingUp, TrendingDown, AlertTriangle, Users, FileText,
  Shield, Banknote, BarChart3, Zap, Receipt,
  Sparkles,
  PieChart,
  CalendarClock,
  ShieldCheck,
  Percent,
  GitCompare,
  LineChart,
  CalendarCheck,
  Copy,
  Bell,
  Activity,
  Calendar,
  Coins,
  PiggyBank,
  Package,
} from 'lucide-react';
import type { WidgetDefinition } from './types';
import {
  RevenueMonthWidget,
  OutstandingWidget,
  DsoWidget,
  CashPositionWidget,
  AgingWidget,
  TopDebitorsWidget,
  OverdueInvoicesWidget,
  RevenueChartWidget,
  TopClientsWidget,
  RecentInvoicesWidget,
  AnafStatusWidget,
  QuickActionsWidget,
  CashFlowForecastWidget,
  WhatIfWidget,
  VatDonutWidget,
  FiscalCalendarWidget,
  SpvStatusWidget,
  CollectionRateWidget,
  YoyComparisonWidget,
  OverdueTrendWidget,
  UpcomingRecurringWidget,
  DetectedRecurringWidget,
  AlertsCenterWidget,
  ActivityFeedWidget,
  InvoicedTodayWidget,
  CollectedTodayWidget,
  CollectedMonthWidget,
  OverdueKpiWidget,
  PendingStockDecrementWidget,
} from './implementations';

export const WIDGET_ORDER = [
  'cash-position',
  'revenue-month',
  'invoiced-today',
  'collected-today',
  'collected-month',
  'dso',
  'outstanding',
  'overdue-kpi',
  'pending-stock-decrement',
  'aging',
  'top-debitors',
  'overdue-invoices',
  'cashflow-forecast',
  'whatif',
  'revenue-chart',
  'top-clients',
  'recent-invoices',
  'anaf-status',
  'vat-donut',
  'fiscal-calendar',
  'spv-status',
  'collection-rate',
  'yoy-comparison',
  'overdue-trend',
  'alerts',
  'upcoming-recurring',
  'detected-recurring',
  'activity-feed',
  'quick-actions',
];

export const WIDGET_REGISTRY: Record<string, WidgetDefinition> = {
  'cash-position': {
    id: 'cash-position',
    title: 'Numerar · lună',
    description: 'Totalul încasărilor din luna curentă (cheltuielile vor fi scăzute într-o versiune viitoare)',
    category: 'kpi',
    size: 'M',
    isNew: true,
    icon: Wallet,
    Component: CashPositionWidget,
  },
  'revenue-month': {
    id: 'revenue-month',
    title: 'Facturat · lună',
    description: 'Total facturat în luna curentă',
    category: 'kpi',
    size: 'S',
    icon: TrendingUp,
    Component: RevenueMonthWidget,
  },
  'invoiced-today': {
    id: 'invoiced-today',
    title: 'Facturat azi',
    description: 'Suma facturată astăzi (fără ciorne și facturi anulate)',
    category: 'kpi',
    size: 'S',
    isNew: true,
    icon: Calendar,
    Component: InvoicedTodayWidget,
  },
  'collected-today': {
    id: 'collected-today',
    title: 'Încasat azi',
    description: 'Plățile încasate astăzi',
    category: 'kpi',
    size: 'S',
    isNew: true,
    icon: Coins,
    Component: CollectedTodayWidget,
  },
  'collected-month': {
    id: 'collected-month',
    title: 'Încasat · lună',
    description: 'Total încasat în luna curentă',
    category: 'kpi',
    size: 'S',
    isNew: true,
    icon: PiggyBank,
    Component: CollectedMonthWidget,
  },
  'dso': {
    id: 'dso',
    title: 'Timp mediu de încasare',
    description: 'În câte zile, în medie, îți încasezi facturile',
    category: 'kpi',
    size: 'S',
    isNew: true,
    icon: Clock,
    Component: DsoWidget,
  },
  'outstanding': {
    id: 'outstanding',
    title: 'Facturi neîncasate',
    description: 'Toate facturile neplătite (în termen sau cu scadența depășită)',
    category: 'kpi',
    size: 'S',
    icon: Wallet,
    Component: OutstandingWidget,
  },
  'overdue-kpi': {
    id: 'overdue-kpi',
    title: 'Facturi cu scadența depășită',
    description: 'Total facturi cu scadența depășită și neîncasate',
    category: 'overdue',
    size: 'S',
    isNew: true,
    icon: AlertTriangle,
    Component: OverdueKpiWidget,
  },
  'pending-stock-decrement': {
    id: 'pending-stock-decrement',
    title: 'Stoc nedescărcat',
    description: 'Facturi emise pentru care stocul încă nu a fost descărcat',
    category: 'ops',
    size: 'S',
    isNew: true,
    icon: Package,
    Component: PendingStockDecrementWidget,
  },
  'aging': {
    id: 'aging',
    title: 'Restanțe pe vechime',
    description: 'Facturi neîncasate grupate pe vechime: 0–30, 31–60, 61–90 și peste 90 de zile',
    category: 'overdue',
    size: 'M',
    isNew: true,
    icon: TrendingDown,
    Component: AgingWidget,
  },
  'top-debitors': {
    id: 'top-debitors',
    title: 'Top debitori',
    description: 'Clienți cu cele mai mari restanțe',
    category: 'overdue',
    size: 'L',
    isNew: true,
    icon: AlertTriangle,
    Component: TopDebitorsWidget,
  },
  'overdue-invoices': {
    id: 'overdue-invoices',
    title: 'Facturi cu scadența depășită',
    description: 'Primele 5 facturi neîncasate cu scadența depășită',
    category: 'overdue',
    size: 'L',
    icon: Clock,
    Component: OverdueInvoicesWidget,
  },
  'cashflow-forecast': {
    id: 'cashflow-forecast',
    title: 'Prognoză încasări · 90 de zile',
    description: 'Ce urmează să încasezi față de ce ai de plătit în următoarele 90 de zile',
    category: 'cashflow',
    size: 'XL',
    isNew: true,
    icon: TrendingUp,
    Component: CashFlowForecastWidget,
  },
  'whatif': {
    id: 'whatif',
    title: 'Simulator „ce-ar fi dacă"',
    description: 'Vezi ce s-ar schimba dacă recuperezi restanțele sau modifici termenul de plată',
    category: 'cashflow',
    size: 'L',
    isNew: true,
    icon: Sparkles,
    Component: WhatIfWidget,
  },
  'revenue-chart': {
    id: 'revenue-chart',
    title: 'Evoluție facturat',
    description: 'Total facturat lunar — ultimele 12 luni',
    category: 'sales',
    size: 'XL',
    icon: BarChart3,
    Component: RevenueChartWidget,
  },
  'top-clients': {
    id: 'top-clients',
    title: 'Top clienți',
    description: 'Clienții cu cel mai mare total facturat',
    category: 'sales',
    size: 'L',
    isNew: true,
    icon: Users,
    Component: TopClientsWidget,
  },
  'recent-invoices': {
    id: 'recent-invoices',
    title: 'Documente recente',
    description: 'Ultimele 5 documente emise (factură, proformă, aviz, storno)',
    category: 'sales',
    size: 'L',
    icon: FileText,
    Component: RecentInvoicesWidget,
  },
  'anaf-status': {
    id: 'anaf-status',
    title: 'Situația e-Factura',
    description: 'Facturi de trimis, în procesare și validate la ANAF',
    category: 'anaf',
    size: 'M',
    icon: Shield,
    Component: AnafStatusWidget,
  },
  'vat-donut': {
    id: 'vat-donut',
    title: 'TVA · lună',
    description: 'TVA colectat, deductibil și de plată',
    category: 'anaf',
    size: 'M',
    isNew: true,
    icon: PieChart,
    Component: VatDonutWidget,
  },
  'fiscal-calendar': {
    id: 'fiscal-calendar',
    title: 'Calendar fiscal',
    description: 'Următoarele termene fiscale ale firmei, pe zile lucrătoare',
    category: 'anaf',
    size: 'M',
    isNew: true,
    icon: CalendarClock,
    Component: FiscalCalendarWidget,
  },
  'spv-status': {
    id: 'spv-status',
    title: 'Conexiunea ANAF (SPV)',
    description: 'Starea conectării la ANAF și când expiră accesul',
    category: 'anaf',
    size: 'M',
    isNew: true,
    icon: ShieldCheck,
    Component: SpvStatusWidget,
  },
  'collection-rate': {
    id: 'collection-rate',
    title: 'Rată încasare',
    description: 'Cât la sută din facturat ai încasat, cu evoluția pe 6 luni',
    category: 'kpi',
    size: 'M',
    isNew: true,
    icon: Percent,
    Component: CollectionRateWidget,
  },
  'yoy-comparison': {
    id: 'yoy-comparison',
    title: 'Luna curentă vs media',
    description: 'Facturatul lunii curente față de media ultimelor 11 luni',
    category: 'sales',
    size: 'M',
    isNew: true,
    icon: GitCompare,
    Component: YoyComparisonWidget,
  },
  'overdue-trend': {
    id: 'overdue-trend',
    title: 'Tendință restanțe',
    description: 'Cum au evoluat restanțele în ultimele 6 luni',
    category: 'overdue',
    size: 'L',
    isNew: true,
    icon: LineChart,
    Component: OverdueTrendWidget,
  },
  'alerts': {
    id: 'alerts',
    title: 'Alerte active',
    description: 'Termene ANAF, facturi respinse și restanțe importante',
    category: 'ai',
    size: 'L',
    isNew: true,
    icon: Bell,
    Component: AlertsCenterWidget,
  },
  'upcoming-recurring': {
    id: 'upcoming-recurring',
    title: 'Facturi programate',
    description: 'Facturile recurente care se emit automat în următoarele 14 zile',
    category: 'ops',
    size: 'L',
    isNew: true,
    icon: CalendarCheck,
    Component: UpcomingRecurringWidget,
  },
  'detected-recurring': {
    id: 'detected-recurring',
    title: 'Facturi repetitive detectate',
    description: 'Clienți cărora le facturezi regulat — poți automatiza cu o factură recurentă',
    category: 'ai',
    size: 'L',
    isNew: true,
    icon: Copy,
    Component: DetectedRecurringWidget,
  },
  'activity-feed': {
    id: 'activity-feed',
    title: 'Activitate recentă',
    description: 'Ultimele acțiuni: facturi, proforme, avize, chitanțe, plăți, NIR-uri',
    category: 'ops',
    size: 'Full',
    isNew: true,
    icon: Activity,
    Component: ActivityFeedWidget,
  },
  'quick-actions': {
    id: 'quick-actions',
    title: 'Acțiuni rapide',
    description: 'Comenzi rapide pentru acțiunile frecvente',
    category: 'ops',
    size: 'L',
    icon: Zap,
    Component: QuickActionsWidget,
  },
};
